import React from "react";
import { PARTNERS, ASSOCIATES, ADMINISTRATIVE_STAFF, MAJOR_CASES, COURTHOUSE_IMG, OFFICE_IMG } from "../data/firmContent";
import useSEO from "../lib/useSEO";

export default function About() {
    useSEO({ title: "About the Chamber", description: "Established 1997. Counsel to government, regulators and financial institutions on Nigeria's most consequential matters.", path: "/about" });
    return (
        <div data-testid="page-about">
            {/* HERO */}
            <section className="relative bg-[#0F172A] text-[#faf9f6]" data-testid="about-hero">
                <div className="absolute inset-0">
                    <img src={COURTHOUSE_IMG} alt="Courthouse" className="w-full h-full object-cover opacity-30" />
                    <div className="absolute inset-0 rj-overlay" />
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
                    <div className="font-mono-cap text-xs text-[#c5a059] mb-4">About the Chamber</div>
                    <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl text-white">
                        Established in 1997 <span className="text-[#c5a059]">with a resolve to deliver legal services of international calibre.</span>
                    </h1>
                </div>
            </section>

            {/* FIRM HISTORY */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="about-history">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-5">
                        <div className="font-mono-cap text-xs text-[#c5a059] mb-3">The Firm</div>
                        <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-tight">
                            Built on a single conviction.
                        </h2>
                    </div>
                    <div className="md:col-span-7 md:pl-8 space-y-5 text-slate-800 leading-relaxed text-[16px] lg:text-[17px]">
                        <p>
                            Rotimi Jacobs &amp; Co., Legal Practitioners was established in 1997 with the resolve to provide
                            qualitative legal services in adherence to international best practices. Our goal is providing
                            innovative, competent, cost-effective and well-timed solutions to issues presented by clients.
                            We render legal services to governments at various levels and their agencies, public and private
                            companies, Nigerian and foreign investors and financial institutions.
                        </p>
                        <p>
                            Our firm is a leading light in several legal engagements across asset tracing, white-collar crime
                            litigation, real estate, industrial relations, commercial transactions and litigation. Recently,
                            we successfully handled — from the High Court up to the Supreme Court — a number of high-profile
                            and sensitive cases including <em>FRN v. Orji Uzor Kalu</em>, <em>FRN v. Jolly Nyame</em> and{" "}
                            <em>FRN v. Joshua Dariye</em>, in which former governors of Abia, Taraba and Plateau States were
                            prosecuted and sentenced to several terms of imprisonment ranging from 10 to 12 years.
                        </p>
                        <p>
                            The firm has also prosecuted Mr. Tafa Balogun (former Inspector General of Police), Chief DSP
                            Alamieseigha (former Governor of Bayelsa State), Dr. Lucky Igbinedion (former Governor of Edo
                            State), <em>FRN v. Alex Badeh</em> and <em>FRN v. Olubukola Saraki</em>. We acted as counsel to
                            the Federal Government in <em>FGN v. AG, Delta State</em>, in which Nigeria successfully claimed
                            the $15 million bribe offered by James Ibori to Mallam Nuhu Ribadu.
                        </p>
                    </div>
                </div>
            </section>

            {/* PARTNERS */}
            <section className="bg-[#f3f1ea] border-y border-slate-900/10 py-20 lg:py-28" data-testid="about-partners">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="font-mono-cap text-xs text-[#c5a059] mb-3">Our People · Partners</div>
                    <h2 className="font-serif-display text-3xl md:text-5xl mb-12">Principal &amp; Partners</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-900/10">
                        {PARTNERS.map((p) => (
                            <article
                                key={p.name}
                                className="group bg-[#faf9f6] p-8 lg:p-10 flex flex-col transition duration-300 hover:bg-[#0F172A] hover:shadow-lg hover:text-[#faf9f6]"
                                data-testid={`partner-card-${p.name.replace(/\s/g, "-")}`}
                            >
                                {p.image && (
                                    <div className="mb-6 overflow-hidden border border-slate-900/10">
                                        <img
                                            src={p.image}
                                            alt={p.name}
                                            className="w-full h-72 object-cover object-top transition duration-300 group-hover:scale-105"
                                        />
                                    </div>
                                )}
                                <div className="rj-rule-gold w-12 mb-5" />
                                <h3 className="font-serif-display text-2xl text-slate-900 transition-colors duration-300 group-hover:text-[#c5a059]">{p.name}</h3>
                                <div className="font-mono-cap text-[11px] text-[#c5a059] mt-2 transition-colors duration-300 group-hover:text-[#faf9f6]">{p.role}</div>
                                <p className="mt-5 text-[15px] text-slate-700 leading-relaxed flex-1 transition-colors duration-300 group-hover:text-[#faf9f6]">{p.bio}</p>
                                {p.linkedin && (
                                    <a
                                        href={p.linkedin}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid={`partner-linkedin-${p.name.replace(/\s/g, "-")}`}
                                        className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-slate-900 border-b border-[#c5a059] pb-1 self-start transition-colors duration-300 group-hover:text-[#faf9f6] hover:text-[#c5a059]"
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                                        LinkedIn profile
                                    </a>
                                )}
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* ASSOCIATES */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="about-associates">
                <div className="font-mono-cap text-xs text-[#c5a059] mb-3">Our People · Associates</div>
                <h2 className="font-serif-display text-3xl md:text-5xl mb-12">Associates</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    {ASSOCIATES.map((a) => (
                        <article
                            key={a.name}
                            className="border border-slate-900/10 bg-[#faf9f6] overflow-hidden flex flex-col md:flex-row transition duration-300 hover:-translate-y-1 hover:shadow-lg"
                            data-testid={`associate-card-${a.name.replace(/\s|\./g, "-")}`}
                        >
                            {a.image ? (
                                <div className="md:w-2/5 bg-[#f3f1ea]">
                                    <img src={a.image} alt={a.name} className="w-full h-56 md:h-full object-cover object-top" />
                                </div>
                            ) : (
                                <div className="md:w-2/5 bg-[#f3f1ea] flex items-center justify-center min-h-[180px]">
                                    <div className="font-serif-display text-5xl text-[#c5a059]/40">
                                        {a.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                                    </div>
                                </div>
                            )}
                            <div className="p-7 md:w-3/5 flex flex-col">
                                <h3 className="font-serif-display text-2xl text-slate-900">{a.name}</h3>
                                <div className="font-mono-cap text-[11px] text-[#c5a059] mt-2">{a.role}</div>
                                <p className="mt-4 text-[15px] text-slate-800 leading-relaxed flex-1">{a.bio}</p>
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            {/* ADMINISTRATIVE STAFF */}
            <section className="bg-[#faf9f6] border-y border-slate-900/10 py-20 lg:py-28" data-testid="about-administrative-staff">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="font-mono-cap text-xs text-[#c5a059] mb-3">Our People · Administrative Staff</div>
                    <h2 className="font-serif-display text-3xl md:text-5xl mb-12">Administrative Staff</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-900/10">
                        {ADMINISTRATIVE_STAFF.map((staff) => (
                            <article
                                key={staff.name}
                                className="group bg-[#faf9f6] p-8 lg:p-10 flex flex-col transition duration-300 hover:bg-[#0F172A] hover:shadow-lg hover:text-[#faf9f6]"
                            >
                                <div className="rj-rule-gold w-12 mb-5" />
                                <h3 className="font-serif-display text-2xl text-slate-900 transition-colors duration-300 group-hover:text-[#c5a059]">
                                    {staff.name}
                                </h3>
                                <div className="font-mono-cap text-[11px] text-[#c5a059] mt-2 transition-colors duration-300 group-hover:text-[#faf9f6]">
                                    {staff.role}
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* MAJOR CASES */}
            <section className="bg-[#0F172A] text-[#faf9f6] py-20" data-testid="about-cases">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="font-mono-cap text-xs text-[#c5a059] mb-3">Selected Matters</div>
                    <h2 className="font-serif-display text-3xl md:text-5xl mb-12">
                        Engagements of consequence.
                    </h2>
                    <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-10">
                        {MAJOR_CASES.map((c) => (
                            <li key={c} className="border-l-2 border-[#c5a059] pl-4 py-1 text-sm text-[#faf9f6]/90">
                                {c}
                            </li>
                        ))}
                    </ul>
                </div>
            </section>
        </div>
    );
}
